/* Partners · /partners — the institutional backbone behind verqo. */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Landmark, ShieldCheck, Truck, Banknote, ArrowRight, ExternalLink } from "lucide-react";
import { VerqoLockup } from "../components/VerqoLockup";
import MarketingMobileNav from "../components/MarketingMobileNav";

const PARTNERS = [
  {
    category: "Banking",
    icon: Landmark,
    blurb: "Our sponsor bank. They hold the FBO accounts that segregate customer funds from operating capital, and they're the rail every dollar settles across.",
    members: [
      {
        name: "U.S. Bank",
        role: "Sponsor bank · FBO holder · ACH and wire originator",
        url: "https://www.usbank.com",
        note: "FDIC-insured · One of the largest banks in the U.S. (U.S. Bancorp) · Member of every major US bank network.",
      },
    ],
  },
  {
    category: "Credit insurance",
    icon: ShieldCheck,
    blurb: "Every approved Net-30/60/90 invoice is insured. This is the layer that makes verqo's credit decisions safe to extend without personal guarantees.",
    members: [
      {
        name: "Allianz Trade",
        role: "Trade credit insurance · buyer-default coverage",
        url: "https://www.allianz-trade.com",
        note: "The world's largest trade credit insurer. €1+ trillion of insured transactions annually.",
      },
    ],
  },
  {
    category: "Lending",
    icon: Banknote,
    blurb: "When a seller chooses Instant Payout (cash today instead of waiting on Net-X), our institutional lending partner funds the advance. verqo isn't the lender — we route, they fund.",
    members: [
      {
        name: "Capec",
        role: "Institutional invoice-financing partner",
        url: "https://capec.io",
        note: "Funds same-day payouts on factored invoices. Underwrites at the buyer level, not the seller — so growing brands aren't penalized for needing the cash.",
      },
    ],
  },
  {
    category: "Shipping & logistics",
    icon: Truck,
    blurb: "Every parcel and freight shipment booked through verqo runs over the same rails your competitors pay 20% more for. We negotiate the rates; you keep them.",
    members: [
      {
        name: "USPS",
        role: "Domestic parcel · ground · priority",
        url: "https://www.usps.com",
        note: "Cheapest domestic option for SMB-sized parcels.",
      },
      {
        name: "UPS",
        role: "Domestic + international parcel · time-definite",
        url: "https://www.ups.com",
        note: "Best mix of speed and coverage for branded wholesale orders.",
      },
      {
        name: "Freight (LTL · FTL)",
        role: "Multi-carrier freight network",
        url: null,
        note: "Less-than-truckload and full-truckload through our freight aggregator — every major US LTL carrier is rate-shopped in one quote.",
      },
    ],
  },
];

export default function Partners() {
  useEffect(() => { document.title = "Partners · verqo"; window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-[#f6f7f5] text-slate-900">
      <header className="px-6 md:px-12 max-w-[1280px] mx-auto pt-8 pb-4 flex items-center justify-between" data-testid="partners-header">
        <Link to="/" className="inline-flex items-center"><VerqoLockup size="sm" /></Link>
        <nav className="hidden lg:flex items-center gap-6 text-[12px] text-slate-600">
          <Link to="/" className="hover:text-slate-900">For sellers</Link>
          <Link to="/buyers" className="hover:text-slate-900">For buyers</Link>
          <Link to="/about" className="hover:text-slate-900">About</Link>
          <Link to="/contact" className="hover:text-slate-900">Contact</Link>
        </nav>
        <MarketingMobileNav />
      </header>

      <section className="px-6 md:px-12 max-w-[920px] mx-auto pt-16 md:pt-24 pb-12" data-testid="partners-hero">
        <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.25em] text-lime-700 mb-5">
          <span className="w-2 h-2 rounded-full bg-lime-500"/>Partners
        </div>
        <h1 className="text-[40px] md:text-[60px] leading-[0.98] tracking-tight font-semibold">
          We don't pretend <br/><span className="text-slate-500">to be the bank.</span>
        </h1>
        <p className="mt-7 max-w-2xl text-[15.5px] md:text-[16.5px] leading-relaxed text-slate-600">
          The best operators in the world don't try to do everything themselves —
          they partner with the people who already do it best. verqo is the
          orchestration layer on top of <strong className="text-slate-900">a chartered sponsor bank</strong>,
          {" "}<strong className="text-slate-900">the world's largest credit insurer</strong>,
          {" "}<strong className="text-slate-900">an institutional lending partner</strong>, and
          {" "}<strong className="text-slate-900">every major shipping carrier</strong>. You get the
          benefit of all four without managing four contracts.
        </p>
      </section>

      <section className="px-6 md:px-12 max-w-[1080px] mx-auto pb-16 md:pb-24" data-testid="partners-grid">
        {PARTNERS.map((cat, ci) => {
          const Icon = cat.icon;
          return (
            <div key={ci} className="mb-12 md:mb-16" data-testid={`partner-cat-${ci}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-slate-900 grid place-items-center">
                  <Icon className="w-5 h-5 text-lime-300"/>
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500">{cat.category}</div>
              </div>
              <p className="max-w-2xl text-[14.5px] md:text-[15px] leading-relaxed text-slate-600 mb-7">
                {cat.blurb}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {cat.members.map((m, mi) => (
                  <div key={mi}
                       className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6"
                       data-testid={`partner-${ci}-${mi}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[15px] md:text-[16px] font-semibold tracking-tight text-slate-900">{m.name}</div>
                        <div className="mt-1 text-[12px] uppercase tracking-[0.15em] text-slate-500">{m.role}</div>
                      </div>
                      {m.url && (
                        <a href={m.url} target="_blank" rel="noreferrer"
                           className="text-slate-400 hover:text-slate-900 transition-colors"
                           data-testid={`partner-link-${ci}-${mi}`}>
                          <ExternalLink className="w-4 h-4"/>
                        </a>
                      )}
                    </div>
                    {m.note && (
                      <p className="mt-3 text-[13px] leading-relaxed text-slate-600">{m.note}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      <section className="px-6 md:px-12 max-w-[920px] mx-auto pb-24" data-testid="partners-want-in">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 md:p-12">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-lime-700 mb-3">Become a partner</div>
          <h2 className="text-[24px] md:text-[32px] leading-tight tracking-tight font-semibold text-slate-900">
            Build with us.
          </h2>
          <p className="mt-3 max-w-xl text-[14.5px] md:text-[15px] text-slate-600 leading-relaxed">
            Are you a sponsor bank, lender, insurer, or logistics network looking to
            plug into a real B2B wholesale flow? We're selective about partners —
            we choose for substance, not optics — but we always read the email.
          </p>
          <Link to="/contact?topic=partnership"
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-900 text-white text-[13.5px] font-semibold hover:opacity-90 transition-opacity"
                data-testid="partners-cta">
            Reach out <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </section>

      <footer className="px-6 md:px-12 max-w-[1280px] mx-auto py-10 border-t border-slate-200 flex flex-wrap gap-4 items-center justify-between text-[11px] text-slate-500" data-testid="partners-footer">
        <div className="flex items-center gap-3"><VerqoLockup size="sm" /><span>· Wholesale, instant.</span></div>
        <div className="flex items-center gap-5">
          <Link to="/" className="hover:text-slate-900">Sellers</Link>
          <Link to="/buyers" className="hover:text-slate-900">Buyers</Link>
          <Link to="/about" className="hover:text-slate-900">About</Link>
          <Link to="/faq" className="hover:text-slate-900">FAQ</Link>
          <Link to="/partners" className="hover:text-slate-900">Partners</Link>
          <Link to="/contact" className="hover:text-slate-900">Contact</Link>
        </div>
        <div>© 2026 verqo</div>
      </footer>
    </div>
  );
}
